import React from 'react';
import { useChecklist, ChecklistStep } from '../contexts/ChecklistContext';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
    CheckCircle, Circle, ArrowUpRight,
    Upload, Crown, BarChart2, Search,
    Share2, FileText, PenTool, HelpCircle
} from 'lucide-react';
import { showDemoIframe } from '../components/editor/DocumentHistory';

const getIconForStep = (key: string, completed: boolean) => {
    const iconProps = {
        size: 20,
        className: completed ? 'text-green-500' : 'text-gray-400'
    };

    switch (key) {
        case 'complete_first_query':
            return <Search {...iconProps} />;
        case 'upload_documents':
            return <Upload {...iconProps} />;
        case 'upgrade_to_pro':
            return <Crown {...iconProps} />;
        case 'conduct_bibliometric_analysis':
            return <BarChart2 {...iconProps} />;
        case 'deep_research_query':
            return <Search {...iconProps} />;
        case 'share_result':
            return <Share2 {...iconProps} />;
        case 'extract_data':
            return <FileText {...iconProps} />;
        case 'create_diagram':
            return <PenTool {...iconProps} />;
        case 'ai_editor':
            return <PenTool {...iconProps} />;
        default:
            return <Circle {...iconProps} />;
    }
};

// Helper function to get instructions for each step
const getInstructionsForStep = (key: string) => {
    switch (key) {
        case 'complete_first_query':
            return "Type a research query in the search bar and press enter.";
        case 'upload_documents':
            return "Go to the Library tab and click the Upload button.";
        case 'upgrade_to_pro':
            return "Click here to upgrade.";
        case 'conduct_bibliometric_analysis':
            return "Navigate to Library or a previous literature review, select sources, and click the bibliometric analysis button.";
        case 'deep_research_query':
            return "Go to Literature Review and select the Deep Research option when creating a new query.";
        case 'share_result':
            return "Open any past literature review and click 'share privately' or 'share publicly' buttons.";
        case 'extract_data':
            return "Go to Library, select sources, and click the Extract Data button.";
        case 'create_diagram':
            return "Navigate to the Diagram tab and create a new visualization of your research.";
        case 'ai_editor':
            return "Go to Documents tab and create or open a document to use the AI Writer.";
        default:
            return null;
    }
};

interface UserChecklistProps {
    showCompleted?: boolean;
    maxItems?: number;
}

const UserChecklist: React.FC<UserChecklistProps> = ({
    showCompleted = true,
    maxItems
}) => {
    const {
        getAllSteps,
        getCompletedSteps,
        getPendingSteps,
        isStepCompleted,
        getProgress
    } = useChecklist();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const progress = getProgress();
    const completedSteps = getCompletedSteps();
    const pendingSteps = getPendingSteps();

    // Determine which steps to show based on props
    let stepsToShow: ChecklistStep[] = [];
    if (showCompleted) {
        stepsToShow = getAllSteps();
    } else {
        stepsToShow = pendingSteps;
    }

    // Limit number of items if maxItems is specified
    if (maxItems && stepsToShow.length > maxItems) {
        stepsToShow = stepsToShow.slice(0, maxItems);
    }

    const handleShowDemo = (event: React.MouseEvent) => {
        event.stopPropagation();
        showDemoIframe('https://app.supademo.com/embed/cm7mbj5en1w0cddumsngnkr1a?embed_v=2');
    };

    const getActionForStep = (step: ChecklistStep) => {
        if (!id) return null;

        switch (step.key) {
            case 'complete_first_query':
                return () => navigate(`/${id}/ask-answerthis?tab=review`);
            case 'upload_documents':
                return () => navigate(`/${id}/ask-answerthis?tab=library`);
            case 'upgrade_to_pro':
                return () => navigate(`/${id}/pricing`);
            case 'conduct_bibliometric_analysis':
                return () => navigate(`/${id}/ask-answerthis?tab=library`);
            case 'deep_research_query':
                return () => navigate(`/${id}/ask-answerthis?tab=review`);
            case 'share_result':
                return () => navigate(`/${id}/ask-answerthis?tab=review`);
            case 'extract_data':
                return () => navigate(`/${id}/ask-answerthis?tab=library`);
            case 'create_diagram':
                return () => navigate(`/${id}/ask-answerthis?tab=diagram`);
            case 'ai_editor':
                return () => navigate(`/${id}/ask-answerthis?tab=documents`);
            default:
                return null;
        }
    };

    return (
        <div className="rounded-lg w-full">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Your Progress</h3>
                <div className="text-sm font-medium text-gray-500">
                    {completedSteps.length} of {getAllSteps().length} completed
                </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-gray-100 rounded-full mb-6">
                <div
                    className="h-2 bg-gradient-to-r from-brandMaroon to-pink-600 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Steps */}
            <div className="space-y-2.5">
                {stepsToShow.map((step) => {
                    const completed = isStepCompleted(step.id);
                    const action = getActionForStep(step);
                    const instructions = getInstructionsForStep(step.key);
                    const isPremiumStep = step.key === 'upgrade_to_pro';
                    const showHelpButton = step.key === 'ai_editor';

                    return (
                        <div
                            key={step.id}
                            className={`flex items-start p-3 border rounded-lg transition-all ${completed
                                ? 'border-green-100 bg-green-50'
                                : isPremiumStep
                                    ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 shadow-sm'
                                    : 'border-gray-200 hover:border-gray-300'
                                } ${action ? 'cursor-pointer' : ''}`}
                            onClick={action ? () => action() : undefined}
                        >
                            <div className="flex-shrink-0 mt-0.5 mr-3">
                                {completed ? (
                                    <CheckCircle className="text-green-500" size={20} />
                                ) : (
                                    getIconForStep(step.key, completed)
                                )}
                            </div>

                            <div className="flex-grow">
                                <div className="flex items-center">
                                    <h4 className={`font-medium ${completed
                                        ? 'text-green-700'
                                        : isPremiumStep
                                            ? 'text-amber-800'
                                            : 'text-gray-800'
                                        }`}>
                                        {step.title}
                                        {isPremiumStep && !completed && (
                                            <span className="ml-2 inline-block px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">PRO</span>
                                        )}
                                    </h4>

                                    {showHelpButton && (
                                        <button
                                            onClick={handleShowDemo}
                                            className="ml-2 text-gray-500 hover:text-brandMaroon cursor-pointer"
                                        >
                                            <HelpCircle size={16} />
                                        </button>
                                    )}
                                </div>

                                {!completed && instructions && (
                                    <div className={`mt-1 text-xs py-1 px-2 rounded ${isPremiumStep
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-blue-50 text-blue-600'
                                        }`}>
                                        <p>
                                            <span className="font-medium">How to:</span> {instructions}
                                        </p>
                                    </div>
                                )}

                                {step.key === 'ai_editor' && (
                                    <div className="mt-1 text-xs text-gray-600">
                                        Create outlines, expand sections, add citations, and more with AI assistance.
                                    </div>
                                )}
                            </div>

                            {action && !completed && (
                                <div className="flex-shrink-0 ml-2">
                                    <ArrowUpRight className={`${isPremiumStep ? 'text-amber-500' : 'text-gray-400'}`} size={16} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Show more link if maxItems is set */}
            {maxItems && getAllSteps().length > maxItems && (
                <div className="mt-4 text-center">
                    <button
                        onClick={() => navigate(`/${id}/checklist`)}
                        className="text-sm text-brandMaroon hover:underline"
                    >
                        View all tasks
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserChecklist; 